import { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import axios from "axios";

import COUNTRIES from "./country_data";
import GAMECATEGORY from "./game_category";

import "./registration.css";

const API_URL = "https://badmintonnepal.org/api/admin/playerRegister";
// const API_URL = "http://localhost:8006/api/admin/playerRegister";

const INITIAL_FORM = {
  name: "",
  game_event: "",
  dob: "",
  country: "",
  phone_number: "",
  club: "",
  point: "",
};

const generateFiveDigitNumber = () => {
  const randomArray = new Uint32Array(1);
  window.crypto.getRandomValues(randomArray);
  return String(randomArray[0] % 100000).padStart(5, "0");
};

const generatePlayerId = (countryCode) => {
  const year = String(new Date().getFullYear()).slice(-2);
  const randomNumber = generateFiveDigitNumber();
  return `${countryCode}-${year}-${randomNumber}`;
};

function RegistrationForm({ open, onClose }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [isLoading, setIsLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handlePhoneChange = (event) => {
    const value = event.target.value.replace(/\D/g, "").slice(0, 10);
    setForm((previous) => ({
      ...previous,
      phone_number: value,
    }));
  };

  const handlePointChange = (event) => {
    const value = event.target.value;
    if (value === "" || Number(value) >= 0) {
      setForm((previous) => ({
        ...previous,
        point: value,
      }));
    }
  };

  const validateForm = () => {
    const name = form.name.trim();
    // const phone = form.phone_number.trim();

    if (!name) {
      toast.warning("Please enter player name.");
      return false;
    }
    if (!form.game_event) {
      toast.warning("Please select Game Category.");
      return false;
    }
    if (!form.dob) {
      toast.warning("Please select date of birth.");
      return false;
    }
    if (!form.country) {
      toast.warning("Please select country.");
      return false;
    }
    // if (!/^[0-9]{10}$/.test(phone)) {
    //   toast.warning("Please enter a valid 10-digit phone number.");
    //   return false;
    // }

    if (form.point !== "" && Number(form.point) < 0) {
      toast.warning("Please enter a valid point.");
      return false;
    }

    // Fixed timezone comparison for date of birth
    const [year, month, day] = form.dob.split("-").map(Number);
    const selectedDOB = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDOB > today) {
      toast.warning("Date of birth cannot be in the future.");
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setForm(INITIAL_FORM);
  };
//handle register
  const handleRegistration = async (event) => {
    event.preventDefault();

    if (isLoading || !validateForm()) {
      return;
    }

    const u_id_generated = generatePlayerId(form.country);
    
    // Safely prepare default payloads without mutating form state
    const registrationData = {
      u_id_generated,
      name: form.name.trim(),
      game_event: form.game_event,
      dob: form.dob,
      phone_number: form.phone_number.trim(),
      country: form.country,
      club: form.club.trim() || "NBA",
      point: form.point === "" ? 0 : Number(form.point),
    };

    setIsLoading(true);
    try {
      console.log("Detail Records:",registrationData);
      const response = await axios.post(API_URL, registrationData, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });
      if (response.status === 200 || response.status === 201) {
        toast.success(
          `Player Registered Successfully — ${u_id_generated}`,
          {
            position: "top-right",
            autoClose: 3000,
            theme: "light",
          }
        );
        resetForm();
        if (onClose) onClose();
      }
    } catch (error) {
      console.error("Player Registration Error:", error);
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        "Unable to register player. Please try again.";

      if (status === 409) {
        toast.error("Generated Player ID already exists. Please submit again.", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
        });
      } else {
        toast.error(message, {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const todayStr = new Date().toISOString().split("T")[0];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 1, sm: 2 },
          maxHeight: { xs: "96vh", sm: "90vh" },
          margin: { xs: 1, sm: 2 },
        },
      }}
    >
      {/* Loading Overlay */}
      {isLoading && (
        <div className="registration__loading">
          <div className="registration__loading-box">
            <CircularProgress size={38} />
            <span>Registering Player...</span>
          </div>
        </div>
      )}

      {/* Header */}
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: 1, sm: 2.5 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #eee",
        }}
      >
        <Typography
          variant="h6"
          component="span"
          sx={{ 
            fontWeight: 600, 
            fontSize: { xs: "1.1rem", sm: "1.25rem" },
            color: "#001111",
            fontFamily: "Roboto, serif",
            textTransform: "capitalize"
          }}
        >
          Player Registration Form
        </Typography>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Scrollable Body */}
      <DialogContent
        dividers
        sx={{
          p: { xs: 2, sm: 3 },
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary" align="center">
          Enter the player information below to register a new player.
        </Typography>

        <form
          id="registration-form"
          onSubmit={handleRegistration}
          className="registration__form"
          style={{ display: "flex", flexDirection: "column", gap: "16px" }}
        >
          {/* NAME */}
          <div className="registration__field">
            <label htmlFor="name">
              Player Name<span>*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              placeholder="Enter player name"
              value={form.name}
              onChange={handleChange}
              maxLength={100}
              autoComplete="name"
              required
            />
          </div>

          {/* GAME CATEGORY */}
          <div className="registration__field">
            <label htmlFor="game_event">
              Game Category<span>*</span>
            </label>
            <div className="registration__select">
              <select
                id="game_event"
                name="game_event"
                value={form.game_event}
                onChange={handleChange}
                required
              >
                <option value="">Select game category</option>
                {GAMECATEGORY.map((gameCat, index) => (
                  <option key={index} value={gameCat}>
                    {gameCat}
                  </option>
                ))}
              </select>
              <span className="registration__select-arrow">▼</span>
            </div>
          </div>

          {/* DOB */}
          <div className="registration__field">
            <label htmlFor="dob">
              Date of Birth<span>*</span>
            </label>
            <input
              type="date"
              id="dob"
              name="dob"
              value={form.dob}
              onChange={handleChange}
              max={todayStr}
              required
            />
          </div>

          {/* COUNTRY */}
          <div className="registration__field">
            <label htmlFor="country">
              Province<span>*</span>
            </label>
            <div className="registration__select">
              <select
                id="country"
                name="country"
                value={form.country}
                onChange={handleChange}
                required
              >
                <option value="">Select Province</option>
                {COUNTRIES.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
              <span className="registration__select-arrow">▼</span>
            </div>
          </div>

          {/* PHONE */}
          <div className="registration__field">
            <label htmlFor="phone_number">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone_number"
              name="phone_number"
              placeholder="Enter 10-digit phone number"
              value={form.phone_number}
              onChange={handlePhoneChange}
              maxLength={10}
              inputMode="numeric"
              autoComplete="tel"
              // required
            />
            <small>Enter a valid 10-digit phone number.</small>
          </div>

          {/* CLUB */}
          <div className="registration__field">
            <label htmlFor="club">Club</label>
            <input
              type="text"
              id="club"
              name="club"
              placeholder="Enter club name"
              value={form.club}
              onChange={handleChange}
              maxLength={150}
              autoComplete="organization"
            />
          </div>

          {/* POINT */}
          <div className="registration__field">
            <label htmlFor="point">Point</label>
            <input
              type="number"
              id="point"
              name="point"
              placeholder="Enter player point"
              value={form.point}
              onChange={handlePointChange}
              min="0"
              step="1"
              inputMode="numeric"
            />
          </div>

          {/* INFORMATION */}
          <div className="registration__info">
            <div className="registration__info-icon">i</div>
            <div>
              <strong>Player ID</strong>
              <p>
                A unique Player ID will be generated automatically based on the selected
                country, current year, and a unique 5-digit number.
              </p>
              {form.country && (
                <div className="registration__id-example">
                  Example:{" "}
                  <strong>
                    {form.country}-{String(new Date().getFullYear()).slice(-2)}-10246
                  </strong>
                </div>
              )}
            </div>
          </div>

          {/* SUBMIT BUTTON */}
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            className="registration__button"
            sx={{ width: "100%", py: 1.2, mt: 1 }}
          >
            {isLoading ? (
              <>
                <CircularProgress
                  size={20}
                  sx={{ color: "white", marginRight: "10px" }}
                />
                Registering...
              </>
            ) : (
              "Register Player"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default RegistrationForm;